import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { enqueueTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import AttachmentUploadSection from '../../attachment-upload-section.gts';
import { DrawerHeader, DrawerBody } from 'tio-reimagined/components/drawer';
import AttachmentsAnalysisView from '../../approval/drawers/ai-analysis/attachments-analysis-view.gts';
import AttachmentList from '../../../tuition-assistance/forms/upload-attachments/attachment-list.gts';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasAssetModel from 'tio-data/models/tas-asset';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type StoreService from '@ember-data/store';

const CLASSIFICATION_OPTIONS = [
  { label: 'Program Approval Documents', key: 'PROGRAM_APPROVAL_DOCUMENTS' },
  { label: 'Program Completion Documents', key: 'PROGRAM_COMPLETION_DOCUMENTS' },
];

interface ProgramAttachmentsDrawerSignature {
  Args: {
    instance: TasProgramInstanceModel;
    allAttachments: TasAssetModel[];
    disabled?: boolean;
    hideDiscarded?: boolean;
  };
}

export default class ProgramAttachmentsDrawer extends Component<ProgramAttachmentsDrawerSignature> {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;

  @tracked showUploadSection = false;
  @tracked selectedClassification = 'PROGRAM_APPROVAL_DOCUMENTS';

  get sortedAttachments(): TasAssetModel[] {
    const assets = this.args.allAttachments ?? [];
    const notDeleted = assets
      .filter((a) => a.attachmentDeletedAt === null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    if (this.args.hideDiscarded) {
      return notDeleted;
    }

    const deleted = assets.filter((a) => a.attachmentDeletedAt !== null);
    return [...notDeleted, ...deleted];
  }

  get assetSummaries() {
    return this.args.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary) => summary !== null);
  }

  get canUpload(): boolean {
    return !this.args.disabled;
  }

  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
  };

  classificationDidChange = (key: string) => {
    this.selectedClassification = key;
  };

  isDeleted = (asset: TasAssetModel): boolean => {
    return asset.attachmentDeletedAt !== null;
  };

  uploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);
    asset.classification = this.selectedClassification;

    const instance = this.args.instance;

    // @ts-expect-error - Dynamic relationship access
    asset.tasProgramInstances.push(instance);
    // @ts-expect-error - Dynamic relationship access
    instance.tasAssets.push(asset);

    await asset.save();
    await instance.save();

    this.showUploadSection = false;
  });

  deleteAttachment = enqueueTask(async (asset: TasAssetModel) => {
    const attachment = asset.attachments[0];
    if (!attachment) {
      return;
    }
    await attachment.destroyRecord();
    await asset.reload();
  });

  <template>
    <DrawerHeader>
      <h2 class="tio-text-heading text-gray-800">
        {{t "common.tas.approval.drawer.attachments.title" default="Attachments"}}
      </h2>
    </DrawerHeader>

    <DrawerBody>
      <div class="flex flex-col gap-6 p-6">
        <AttachmentUploadSection
          @canUpload={{this.canUpload}}
          @showUploadSection={{this.showUploadSection}}
          @toggleUploadSection={{this.toggleUploadSection}}
          @selectedClassification={{this.selectedClassification}}
          @classificationDidChange={{this.classificationDidChange}}
          @uploadFile={{this.uploadFile}}
          @classificationOptions={{CLASSIFICATION_OPTIONS}}
        />

        {{! Attachments View }}
        {{#if @hideDiscarded}}
          <AttachmentList
            @assets={{this.sortedAttachments}}
            @disabled={{@disabled}}
            @hideDiscarded={{true}}
            class="w-full"
          />
        {{else}}
          <AttachmentsAnalysisView
            @attachments={{this.sortedAttachments}}
            @assetSummaries={{this.assetSummaries}}
            @deleteAttachment={{this.deleteAttachment}}
            @isDeleted={{this.isDeleted}}
          />
        {{/if}}
      </div>
    </DrawerBody>
  </template>
}
